import { Box, CircularProgress } from '@mui/material';
import { createContext, useContext, useEffect, useState } from 'react';
import { useTask, UseTaskReturn } from 'vig-ui-library';
import { getCompanyLogoUrl } from '@/Utils/environment';
import ExpiredLinkView from '@/Views/MagicLink/ExpiredLinkView';
import { GetMagicLinkDataOutput } from '@/Schemas/MagicLink/GetMagicLinkDataOutput';
import { type UploadIdCardOutput } from 'request-form-lib';
import { getMagicLinkData, confirmMagicLink, cancelMagicLink } from '@/Api/MagicLinkApi';
import LinkConfirmedView from '@/Views/MagicLink/LinkConfirmedView';

export enum MagicLinkView {
    INTRO = 0,
    REQUEST_DETAIL = 1,
    CAMERA = 2,
    EXTRACTED_DATA = 3,
    INVALID_ID_CARD = 4,
    ID_CARD_UPLOADED = 5,
    UPLOAD_ID_CARD = 6,
    CANCELLED = 7,
    LINK_CONFIRMED = 8,
}

export type RequestDetail = {
    headline: string;
    description: string;
    attachmentCount: number;
    attachmentCountLabel: string;
    params: {
        section: string | null;
        label: string;
        value: string;
    }[];
};

export type MagicLinkAppProviderState = {
    magicLinkData: GetMagicLinkDataOutput | null;
    requestDetail: RequestDetail | null;
    confirmLinkTask: UseTaskReturn<[], void>;
    cancelRequestTask: UseTaskReturn<[], void>;
    confirmError: string | null;
    currentView: MagicLinkView;
    navigateToView: (view: MagicLinkView) => void;
    getIdCardFrontFileId: () => string | null;
    getIdCardBackFileId: () => string | null;
    frontSideUploadResult: UploadIdCardOutput | null;
    setFrontSideUploadResult: (result: UploadIdCardOutput | null) => void;
    backSideUploadResult: UploadIdCardOutput | null;
    setBackSideUploadResult: (result: UploadIdCardOutput | null) => void;
    getNextSideToCapture: () => 'front' | 'back' | null;
    validationErrorMessage: string | null;
    setValidationErrorMessage: (message: string | null) => void;
};

const MagicLinkAppProviderContext = createContext<MagicLinkAppProviderState>({} as MagicLinkAppProviderState);

export type MagicLinkAppProviderProps = {
    children: React.ReactNode;
};

export const getToken = (): string | null => {
    const match = window.location.pathname.match(/\/request-link\/confirmation\/([^/]+)/);
    return match?.[1] ?? null;
};


const deriveRequestDetail = (magicLinkData: GetMagicLinkDataOutput | null): RequestDetail | null => {
    if (!magicLinkData) {
        return null;
    }

    const attachmentCount = magicLinkData.requestFiles?.length || 0;

    return {
        headline: magicLinkData.requestTypeName || 'Request Detail',
        description: magicLinkData.requestTypeName || '',
        attachmentCount: attachmentCount,
        attachmentCountLabel: `Pripojené súbory: ${attachmentCount}`,
        params: magicLinkData.readableRequestParameters
            ?.filter((param) => !!param?.label && !!param?.value)
            .map((param) => ({
                section: param.section ?? null,
                label: String(param.label),
                value: String(param.value),
            })) || [],
    };
};


export const MagicLinkAppProvider = ({ children }: MagicLinkAppProviderProps) => {
    const [magicLinkData, setMagicLinkData] = useState<GetMagicLinkDataOutput | null>(null);
    const [currentView, setCurrentView] = useState<MagicLinkView>(MagicLinkView.INTRO);
    const [magicLinkDataLoadError, setMagicLinkDataLoadError] = useState<boolean>(false);
    const [confirmError, setConfirmError] = useState<string | null>(null);
    const [frontSideUploadResult, setFrontSideUploadResult] = useState<UploadIdCardOutput | null>(null);
    const [backSideUploadResult, setBackSideUploadResult] = useState<UploadIdCardOutput | null>(null);
    const [validationErrorMessage, setValidationErrorMessage] = useState<string | null>(null);

    const setupSessionExpiration = (data: GetMagicLinkDataOutput) => {
        const sessionExpireAt = data.linkData?.sessionExpireAt;

        // Only set up session expiration timer if sessionExpireAt exists
        // The API already handles link expiration and sets expire: true when appropriate
        if (!sessionExpireAt) {
            return;
        }

        // Parse sessionExpireAt as UTC (it comes from server as UTC+0)
        // If the string doesn't have 'Z' suffix, append it to ensure UTC parsing
        const expireDateString = sessionExpireAt.endsWith('Z') ? sessionExpireAt : `${sessionExpireAt}Z`;
        const expireDate = new Date(expireDateString);
        // Get current time (JavaScript Date stores time as UTC milliseconds internally)
        const now = new Date();

        // Compare UTC timestamps (getTime() returns UTC milliseconds)
        const timeUntilExpiry = expireDate.getTime() - now.getTime();

        if (timeUntilExpiry > 0) {
            setTimeout(() => {
                setMagicLinkData((prevData) => {
                    if (prevData) {
                        return { ...prevData, expire: true };
                    }
                    return prevData;
                });
            }, timeUntilExpiry);
        } else {
            // Session already expired, update the data
            setMagicLinkData((prevData) => {
                if (prevData) {
                    return { ...prevData, expire: true };
                }
                return prevData;
            });
        }
    };


    const loadMagicLinkDataTask = useTask(async () => {
        try {
            // Get linkTokenHash from the last segment of the URL path
            const linkTokenHash = getToken();

            if (!linkTokenHash) {
                setMagicLinkDataLoadError(true);
                return;
            }

            const data = await getMagicLinkData(linkTokenHash);
            setMagicLinkData(data);
            // After loading, redirect to first step
            setCurrentView(MagicLinkView.INTRO);

            // Setup silent countdown for sessionExpireAt
            setupSessionExpiration(data);
        } catch (error: any) {
            setMagicLinkDataLoadError(true);
        }
    });

    const navigateToView = (view: MagicLinkView) => {
        setCurrentView(view);
    };

    const confirmLinkTask = useTask(async () => {
        const linkTokenHash = getToken();

        if (!linkTokenHash) {
            const errorMessage = 'Link token hash is missing from URL path';
            setConfirmError(errorMessage);
            throw new Error(errorMessage);
        }

        try {
            setConfirmError(null);
            await confirmMagicLink(linkTokenHash);

            // On success, navigate to confirmed view
            navigateToView(MagicLinkView.LINK_CONFIRMED);
        } catch (error: any) {
            const errorMessage = error?.response?.data?.errorMessage
                || error?.message
                || 'Nepodarilo sa potvrdiť žiadosť. Skúste to znova.';
            setConfirmError(errorMessage);
            throw error;
        }
    });

    const cancelRequestTask = useTask(async () => {
        const linkTokenHash = getToken();

        if (!linkTokenHash) {
            return;
        }

        await cancelMagicLink(linkTokenHash);

        // Navigate to cancelled view
        setCurrentView(MagicLinkView.CANCELLED);
    });

    useEffect(() => {
        loadMagicLinkDataTask.perform();
    }, []);

    if (loadMagicLinkDataTask.isRunning) {
        return <ApplicationLoader />;
    }

    // Check if response is empty (all fields are null)
    const isEmptyResponse = magicLinkData &&
        magicLinkData.expire === null &&
        magicLinkData.linkData === null &&
        magicLinkData.readableRequestParameters === null &&
        magicLinkData.requestFiles === null &&
        magicLinkData.idCardFrontUploaded === null &&
        magicLinkData.idCardBackUploaded === null &&
        magicLinkData.requestTypeName === null;

    if (magicLinkData?.linkData?.confirmedAt) {
        return <LinkConfirmedView />;
    }

    // If link is expired, session has expired, or response is empty, show expired view directly
    // Only show expired view if expire is explicitly true (not null or false) or if all fields are null
    if (magicLinkData?.expire === true || isEmptyResponse || magicLinkDataLoadError) {
        return <ExpiredLinkView />;
    }

    const requestDetail = deriveRequestDetail(magicLinkData);

    const getIdCardFrontFileId = (): string | null => {
        const idCardFrontFile = magicLinkData?.requestFiles?.find(file => file.taxonomyId === 41);
        return idCardFrontFile?.id || null;
    };

    const getIdCardBackFileId = (): string | null => {
        const idCardBackFile = magicLinkData?.requestFiles?.find(file => file.taxonomyId === 42);
        return idCardBackFile?.id || null;
    };

    const getNextSideToCapture = (): 'front' | 'back' | null => {
        if (!frontSideUploadResult) return 'front';
        if (!backSideUploadResult) return 'back';
        return null; // Both sides uploaded
    };

    const providerState: MagicLinkAppProviderState = {
        magicLinkData,
        requestDetail,
        confirmLinkTask,
        cancelRequestTask,
        confirmError,
        currentView,
        navigateToView,
        getIdCardFrontFileId,
        getIdCardBackFileId,
        frontSideUploadResult,
        setFrontSideUploadResult,
        backSideUploadResult,
        setBackSideUploadResult,
        getNextSideToCapture,
        validationErrorMessage,
        setValidationErrorMessage,
    };

    return (
        <MagicLinkAppProviderContext.Provider value={providerState}>
            {children}
        </MagicLinkAppProviderContext.Provider>
    );
};

export const useMagicLinkAppProvider = () => {
    const context = useContext(MagicLinkAppProviderContext);

    if (!context) {
        throw new Error('useMagicLinkAppProvider must be used within a MagicLinkAppProvider');
    }

    return context;
};

const flexCenter: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
};

const fullScreen: React.CSSProperties = {
    width: '100vw',
    height: '100vh',
};

const ApplicationLoader = () => {
    const logoUrl = getCompanyLogoUrl();

    return (
        <Box sx={{ ...flexCenter, ...fullScreen, gap: 2, p: 2 }}>
            {logoUrl && <img src={logoUrl} width="250" height="auto" alt="Company Logo" />}
            <CircularProgress />
        </Box>
    );
};
